import { useMemo } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { jwtDecode } from 'jwt-decode';
import _ from 'lodash';
import { IndustryType } from '../codegen/graphql';
import ottoBuschLogo from '../assets/img/otto-busch-logo.svg';
import argosBuschLogo from '../assets/img/argos-busch-logo.svg';

export { IndustryType };

/**
 * Reads the logged-in user's OWN client industry type from the decoded Keycloak
 * JWT (`otto_identity.client.industryType`). Falls back to {@link IndustryType.INDUSTRY}
 * when there is no token, decoding fails, or the value is missing/unknown.
 *
 * Use this for app-wide branding tied to the current user (e.g. the app name).
 * For asset/system-specific branding, prefer the owning client's industry type
 * (see {@link getFallbackAssetImage}) — an admin may manage multiple clients with
 * different industry types.
 */
export const useIndustryType = (): IndustryType => {
  const { keycloak } = useKeycloak();
  return useMemo<IndustryType>(() => {
    if (!keycloak.token) return IndustryType.INDUSTRY;
    try {
      const decoded = jwtDecode<Record<string, unknown>>(keycloak.token);
      const value = _.get(decoded, 'otto_identity.client.industryType', IndustryType.INDUSTRY);
      return value === IndustryType.SEMICONDUCTOR ? IndustryType.SEMICONDUCTOR : IndustryType.INDUSTRY;
    } catch {
      return IndustryType.INDUSTRY;
    }
  }, [keycloak.token]);
};

/** Product name shown to the user, branded per the current user's industry type ("ARGOS" vs "OTTO"). */
export const useAppName = (): string =>
  useIndustryType() === IndustryType.SEMICONDUCTOR ? 'ARGOS' : 'OTTO';

/**
 * Default placeholder image for an asset/system, branded by the industry type of
 * the OWNING client (not the logged-in user). Pass the owner's/client's
 * `industryType`; defaults to the INDUSTRY (OTTO) logo when unknown.
 */
export const getFallbackAssetImage = (industryType?: IndustryType | null): string =>
  industryType === IndustryType.SEMICONDUCTOR ? argosBuschLogo : ottoBuschLogo;
